import { useEffect, useRef, useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, Tab, Tabs, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import UndoIcon from '@mui/icons-material/Undo'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

/// Pen colour palette. Black is the default for the "looks-like-pen-on-
/// paper" feel; the rest are common ink colours techs ask for so a
/// signature reads well against a coloured background.
const COLOURS = [
  { name: 'Black', value: '#0F172A' },
  { name: 'Blue',  value: '#1D4ED8' },
  { name: 'Red',   value: '#DC2626' },
  { name: 'Green', value: '#15803D' },
]

interface Props {
  open: boolean
  title?: string
  /// Shown above the canvas, e.g. "Sign to close WO-1024".
  subtitle?: string
  onClose: () => void
  /// Called with the final signature image as a PNG Blob. The caller
  /// is responsible for uploading it via api.workOrders.uploadSignature
  /// and then making the actual close / acknowledge call.
  onConfirm: (blob: Blob) => Promise<void> | void
  busy?: boolean
}

/// Modal that lets a user produce a signature image — either by drawing
/// it on canvas with a chosen pen colour, or by uploading an existing
/// signature file. Mirrors the mobile DigitalSignerPage flow so the
/// resulting blob can be POSTed via the same endpoint.
export function DigitalSigner({
  open, title = 'Digital signature', subtitle, onClose, onConfirm, busy,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [tab, setTab] = useState<'draw' | 'upload'>('draw')
  const [colour, setColour] = useState(COLOURS[0]!.value)
  const [hasInk, setHasInk] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadPreview, setUploadPreview] = useState<string | null>(null)

  // Canvas sizing. The buffer must track the visual size, otherwise
  // pointer coords don't map to where the stroke lands. MUI's open
  // animation means `clientWidth` is 0 on the first tick, so a
  // `ResizeObserver` keeps us in sync as the layout settles. We
  // guard on a real size change so the buffer isn't wiped mid-draw.
  //
  // Drawing math: we DON'T use `ctx.scale(dpr, dpr)`. Instead, every
  // pointer event maps `(clientX, clientY)` into buffer pixels via
  // the current bounding-rect ratio. That keeps strokes glued to
  // the cursor even if the rendered size and the assumed CSS size
  // drift by a pixel or two — which is exactly the offset the user
  // was seeing.
  const lastSize = useRef<{ w: number; h: number }>({ w: 0, h: 0 })
  useEffect(() => {
    if (!open) return
    const c = canvasRef.current
    if (!c) return
    const resize = () => {
      const dpr = window.devicePixelRatio || 1
      const rect = c.getBoundingClientRect()
      const cssW = rect.width
      const cssH = rect.height
      if (!cssW || !cssH) return
      if (lastSize.current.w === cssW && lastSize.current.h === cssH) return
      lastSize.current = { w: cssW, h: cssH }
      c.width  = Math.round(cssW * dpr)
      c.height = Math.round(cssH * dpr)
      const ctx = c.getContext('2d')
      if (!ctx) return
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, c.width, c.height)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      setHasInk(false)
    }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(c)
    return () => {
      ro.disconnect()
      lastSize.current = { w: 0, h: 0 }
    }
  }, [open])

  /// Translate a pointer event into BUFFER pixel coordinates, using
  /// the current bounding rect's ratio to the buffer. This is the
  /// only safe way once the canvas might be CSS-scaled vs its
  /// drawing buffer (different rounding, partial-pixel layout, etc.).
  const toBufferCoords = (c: HTMLCanvasElement, e: React.PointerEvent) => {
    const rect = c.getBoundingClientRect()
    return {
      x: ((e.clientX - rect.left) / rect.width) * c.width,
      y: ((e.clientY - rect.top)  / rect.height) * c.height,
    }
  }

  const last = useRef<{ x: number; y: number } | null>(null)
  const pointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current
    if (!c) return
    c.setPointerCapture(e.pointerId)
    last.current = toBufferCoords(c, e)
  }
  const pointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!last.current) return
    const c = canvasRef.current
    const ctx = c?.getContext('2d')
    if (!c || !ctx) return
    const { x, y } = toBufferCoords(c, e)
    const dpr = window.devicePixelRatio || 1
    ctx.strokeStyle = colour
    // Stroke width in buffer pixels — multiply by dpr so the visual
    // width stays at ~2.4 CSS pixels regardless of device density.
    ctx.lineWidth = 2.4 * dpr
    ctx.beginPath()
    ctx.moveTo(last.current.x, last.current.y)
    ctx.lineTo(x, y)
    ctx.stroke()
    last.current = { x, y }
    if (!hasInk) setHasInk(true)
  }
  const pointerUp = () => { last.current = null }

  const clearCanvas = () => {
    const c = canvasRef.current
    const ctx = c?.getContext('2d')
    if (!c || !ctx) return
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, c.width, c.height)
    setHasInk(false)
  }

  // Undo: clearing the whole canvas — true stroke-level undo would
  // need a stack of paths, which is overkill for a single signature.
  // Users can redraw if they make a mistake.
  const handleUndo = clearCanvas

  const handleFile = (file: File | null) => {
    setUploadFile(file)
    if (uploadPreview) URL.revokeObjectURL(uploadPreview)
    setUploadPreview(file ? URL.createObjectURL(file) : null)
  }

  useEffect(() => () => { if (uploadPreview) URL.revokeObjectURL(uploadPreview) }, [uploadPreview])

  const canConfirm = !busy && (
    (tab === 'draw' && hasInk) || (tab === 'upload' && uploadFile != null)
  )

  const handleConfirm = async () => {
    if (tab === 'draw') {
      const c = canvasRef.current
      if (!c) return
      // Compose onto a fresh canvas with a guaranteed white fill so
      // the saved PNG always carries "ink on paper" pixels. If we
      // just `toBlob` the live canvas we'd risk transparent areas
      // (e.g. if a ResizeObserver re-init landed between fill and
      // capture), which then read as dark blocks when the report or
      // detail card is rendered against a dark theme.
      const out = document.createElement('canvas')
      out.width = c.width
      out.height = c.height
      const octx = out.getContext('2d')
      if (!octx) return
      octx.fillStyle = '#FFFFFF'
      octx.fillRect(0, 0, out.width, out.height)
      octx.drawImage(c, 0, 0)
      const blob: Blob | null = await new Promise((resolve) =>
        out.toBlob((b) => resolve(b), 'image/png'),
      )
      if (!blob) return
      await onConfirm(blob)
    } else if (uploadFile) {
      await onConfirm(uploadFile)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{title}</Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} disabled={busy}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, minHeight: 36 }}>
          <Tab value="draw"   label="Draw"   sx={{ minHeight: 36, textTransform: 'none', fontWeight: 600 }} />
          <Tab value="upload" label="Upload" sx={{ minHeight: 36, textTransform: 'none', fontWeight: 600 }} />
        </Tabs>

        {tab === 'draw' && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              {COLOURS.map((c) => (
                <Box
                  key={c.value}
                  onClick={() => setColour(c.value)}
                  role="button"
                  aria-label={c.name}
                  sx={{
                    width: 26, height: 26, borderRadius: '50%', cursor: 'pointer',
                    background: c.value,
                    boxShadow: colour === c.value
                      ? '0 0 0 3px #fff, 0 0 0 5px #0D9488'
                      : '0 0 0 1px rgba(0,0,0,0.15)',
                    transition: 'box-shadow 120ms ease',
                  }}
                />
              ))}
              <Box sx={{ flex: 1 }} />
              <IconButton size="small" onClick={handleUndo} disabled={!hasInk} title="Clear">
                <UndoIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Box
              sx={{
                position: 'relative', border: '1px dashed', borderColor: 'divider',
                borderRadius: 1.5, bgcolor: '#FFFFFF', height: 220,
              }}
            >
              <canvas
                ref={canvasRef}
                onPointerDown={pointerDown}
                onPointerMove={pointerMove}
                onPointerUp={pointerUp}
                onPointerCancel={pointerUp}
                style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none', cursor: 'crosshair' }}
              />
              {!hasInk && (
                <Box sx={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', pointerEvents: 'none',
                  color: 'text.disabled', fontSize: 13, fontStyle: 'italic',
                }}>
                  Sign here with your mouse, trackpad, or stylus
                </Box>
              )}
            </Box>
          </Stack>
        )}

        {tab === 'upload' && (
          <Stack spacing={1.5} alignItems="stretch">
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
            >
              {uploadFile ? 'Replace image' : 'Choose signature image'}
              <input
                type="file"
                hidden
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            <Box
              sx={{
                border: '1px dashed', borderColor: 'divider', borderRadius: 1.5,
                bgcolor: '#FFFFFF', minHeight: 220, display: 'flex',
                alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
              }}
            >
              {uploadPreview
                ? <Box component="img" src={uploadPreview} alt="signature" sx={{ maxWidth: '100%', maxHeight: 220 }} />
                : <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic' }}>
                    PNG, JPEG, or WebP — transparent background reads best
                  </Typography>}
            </Box>
            {uploadFile && (
              <Button
                size="small"
                onClick={() => handleFile(null)}
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                Remove
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {busy ? 'Saving…' : 'Sign & continue'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
